import { useState, FormEvent, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { adminAPI } from "../../services/api";
import ThemeToggle from "../../components/ThemeToggle/ThemeToggle";
import "./ResetPassword.css";

interface PasswordRequirement {
  id: string;
  label: string;
  test: (password: string) => boolean;
  met: boolean;
}

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [requirements, setRequirements] = useState<PasswordRequirement[]>([
    {
      id: "length",
      label: "At least 8 characters",
      test: (pwd) => pwd.length >= 8,
      met: false,
    },
    {
      id: "uppercase",
      label: "One uppercase letter",
      test: (pwd) => /[A-Z]/.test(pwd),
      met: false,
    },
    {
      id: "lowercase",
      label: "One lowercase letter",
      test: (pwd) => /[a-z]/.test(pwd),
      met: false,
    },
    {
      id: "number",
      label: "One number",
      test: (pwd) => /[0-9]/.test(pwd),
      met: false,
    },
    {
      id: "special",
      label: "One special character",
      test: (pwd) => /[^a-zA-Z0-9]/.test(pwd),
      met: false,
    },
  ]);

  useEffect(() => {
    if (!token) {
      setError(
        "Invalid or missing reset token. Please request a new password reset link."
      );
    }
  }, [token]);

  useEffect(() => {
    // Calculate password strength and check requirements
    let strength = 0;
    const updatedRequirements = requirements.map((req) => {
      const met = req.test(newPassword);
      if (met) strength += 20;
      return { ...req, met };
    });

    setRequirements(updatedRequirements);
    setPasswordStrength(Math.min(strength, 100));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newPassword]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    // Validate all requirements are met
    const allRequirementsMet = requirements.every((req) => req.met);
    if (!allRequirementsMet) {
      setError("Please meet all password requirements");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!token) {
      setError("Invalid reset token");
      return;
    }

    setIsLoading(true);

    try {
      await adminAPI.resetPassword({ token, newPassword });
      setSuccess(true);
      setTimeout(() => {
        navigate("/admin/login");
      }, 3000);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "Failed to reset password. The link may have expired."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getStrengthColor = () => {
    if (passwordStrength <= 40) return "#dc3545";
    if (passwordStrength <= 60) return "#ffc107";
    if (passwordStrength <= 80) return "#0dcaf0";
    return "#198754";
  };

  const getStrengthText = () => {
    if (passwordStrength <= 40) return "Weak";
    if (passwordStrength <= 60) return "Fair";
    if (passwordStrength <= 80) return "Good";
    return "Strong";
  };

  const getStrengthClass = () => {
    if (passwordStrength <= 40) return "strength-weak";
    if (passwordStrength <= 60) return "strength-fair";
    if (passwordStrength <= 80) return "strength-good";
    return "strength-strong";
  };

  // Invalid token error state
  if (!token) {
    return (
      <div className="reset-password-page">
        <div className="theme-toggle-wrapper">
          <ThemeToggle floating />
        </div>

        <div className="reset-password-background">
          <div className="login-shape shape-1"></div>
          <div className="login-shape shape-2"></div>
          <div className="login-shape shape-3"></div>
        </div>

        <div className="reset-password-container">
          <div className="token-error-card">
            <div className="error-icon">⛔</div>
            <h2>Invalid Reset Link</h2>
            <p>The password reset link is invalid or has expired.</p>
            <p>Please request a new password reset link to continue.</p>
            <button
              onClick={() => navigate("/admin/forgot-password")}
              className="btn-primary"
            >
              Request New Link
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="reset-password-page">
        <div className="theme-toggle-wrapper">
          <ThemeToggle floating />
        </div>

        <div className="reset-password-background">
          <div className="login-shape shape-1"></div>
          <div className="login-shape shape-2"></div>
          <div className="login-shape shape-3"></div>
        </div>

        <div className="reset-password-container">
          <div className="success-card">
            <div className="success-icon">✅</div>
            <h2>Password Reset Successful!</h2>
            <p>Your password has been successfully reset.</p>
            <p className="hint">Redirecting to login page...</p>
            <div className="redirect-loader">
              <div className="loader-bar"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main reset password form
  return (
    <div className="reset-password-page">
      <div className="theme-toggle-wrapper">
        <ThemeToggle floating />
      </div>

      <div className="reset-password-background">
        <div className="login-shape shape-1"></div>
        <div className="login-shape shape-2"></div>
        <div className="login-shape shape-3"></div>
      </div>

      <div className="reset-password-container">
        <div className="reset-password-card">
          <div className="card-header">
            <div className="icon-wrapper">
              <span className="icon">🔒</span>
            </div>
            <h1>Reset Password</h1>
            <p>Enter your new password below</p>
          </div>

          <form onSubmit={handleSubmit} className="reset-password-form">
            {error && (
              <div className="alert alert-error">
                <span className="alert-icon">⚠️</span>
                <span>{error}</span>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <div className="input-wrapper">
                <span className="input-icon">🔒</span>
                <input
                  type={showPassword ? "text" : "password"}
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  disabled={isLoading}
                  autoComplete="new-password"
                  autoFocus
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>

              {newPassword && (
                <div className="password-strength">
                  <div className="strength-bar">
                    <div
                      className={`strength-fill ${getStrengthClass()}`}
                      style={{
                        width: `${passwordStrength}%`,
                      }}
                    ></div>
                  </div>
                  <span
                    className="strength-text"
                    style={{ color: getStrengthColor() }}
                  >
                    {getStrengthText()}
                  </span>
                </div>
              )}

              <div className="password-requirements">
                <h4>Password Requirements:</h4>
                <ul className="requirements-list">
                  {requirements.map((req) => (
                    <li
                      key={req.id}
                      className={`requirement-item ${req.met ? "met" : ""}`}
                    >
                      <span className="requirement-icon">
                        {req.met ? "✅" : "⭕"}
                      </span>
                      <span>{req.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="input-wrapper">
                <span className="input-icon">🔒</span>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  disabled={isLoading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                  aria-label={
                    showConfirmPassword ? "Hide password" : "Show password"
                  }
                >
                  {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>

              {confirmPassword && newPassword !== confirmPassword && (
                <span className="field-error">❌ Passwords do not match</span>
              )}

              {confirmPassword &&
                newPassword === confirmPassword &&
                newPassword.length >= 8 && (
                  <span className="passwords-match">
                    <span className="icon">✅</span>
                    Passwords match
                  </span>
                )}
            </div>

            <button
              type="submit"
              className="btn-submit"
              disabled={
                isLoading ||
                newPassword !== confirmPassword ||
                !requirements.every((req) => req.met)
              }
            >
              {isLoading ? (
                <>
                  <span className="spinner"></span>
                  <span>Resetting...</span>
                </>
              ) : (
                <>
                  <span>Reset Password</span>
                  <span className="btn-arrow">→</span>
                </>
              )}
            </button>

            <div className="form-footer">
              <Link to="/admin/login" className="link-back">
                ← Back to Login
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
